import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { API_BASE, pickErr } from "@/lib/api";
import { notifyError } from "@/lib/notify";

function toAbsolute(url) {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  return `${API_BASE}${url.replace(/^\/api/, "")}`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso.slice(0, 10);
  }
}

const STATUS_BADGES = {
  assigned: "bg-slate-100 text-slate-700",
  started: "bg-amber-100 text-amber-800",
  completed: "bg-emerald-100 text-emerald-800",
};

export default function StudentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [reportBusy, setReportBusy] = useState(null); // category_id while busy

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const res = await api.get(`/students/${id}`);
      setData(res.data || null);
    } catch (err) {
      setLoadError(pickErr(err, "Failed to load student"));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const completedHistory = useMemo(
    () => (data?.history || []).filter((h) => h.status === "completed"),
    [data]
  );

  async function generateReport(categoryId) {
    setReportBusy(categoryId);
    try {
      const res = await api.post("/report", {
        student_id: id,
        category_id: categoryId,
      });
      return toAbsolute(res.data?.pdf_url);
    } catch (err) {
      const msg = pickErr(err, "Failed to generate report");
      notifyError(msg);
      return null;
    } finally {
      setReportBusy(null);
    }
  }

  async function onViewReport(categoryId) {
    const url = await generateReport(categoryId);
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  }

  async function onDownloadReport(categoryId) {
    const url = await generateReport(categoryId);
    if (!url) return;
    // Browser already inlines because of Content-Disposition: inline — to force
    // download we open with a download attribute.
    const a = document.createElement("a");
    a.href = url;
    a.download = "";
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  if (loading) {
    return <p className="text-sm text-slate-500">Loading student…</p>;
  }
  if (loadError) {
    return (
      <div>
        <p
          data-testid="student-detail-load-error"
          className="text-sm text-red-600"
        >
          {loadError}
        </p>
        <button
          onClick={() => navigate("/admin/students")}
          className="mt-3 text-sm text-slate-600 hover:text-slate-900"
        >
          ← Back to students
        </button>
      </div>
    );
  }
  if (!data) return null;

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-xl font-semibold text-slate-900">
          {data.name}
        </h2>
        <button
          onClick={() => navigate("/admin/students")}
          data-testid="student-detail-back"
          className="text-sm text-slate-600 hover:text-slate-900"
        >
          ← Back
        </button>
      </div>
      <p className="text-sm text-slate-500 mb-6">
        Student record at{" "}
        <span className="text-slate-700">{data.school_name}</span>
      </p>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_220px] items-start">
        {/* Details */}
        <section
          className="bg-white border border-slate-200 rounded-lg p-5"
          data-testid="student-detail-card"
        >
          <h3 className="text-sm font-semibold text-slate-900 mb-3">
            Student details
          </h3>
          <dl className="grid grid-cols-2 gap-y-2 text-sm">
            <DT label="Student Code" mono value={data.student_code} />
            <DT label="Name" value={data.name} />
            <DT label="Roll number" mono value={data.roll_number} />
            <DT label="Date of birth" value={data.dob} />
            <DT label="Class" value={data.class_name} />
            <DT label="School" value={data.school_name} />
            <DT label="Created" value={fmtDate(data.created_at)} />
          </dl>
        </section>

        {/* Stats */}
        <section className="bg-white border border-slate-200 rounded-lg p-5">
          <h3 className="text-sm font-semibold text-slate-900 mb-3">
            Statistics
          </h3>
          <div className="space-y-3">
            <Stat label="Assigned" value={data.assigned_count ?? 0} />
            <Stat label="Completed" value={data.completed_count ?? 0} />
          </div>
        </section>
      </div>

      {/* History */}
      <section className="mt-6 bg-white border border-slate-200 rounded-lg">
        <div className="px-5 py-3 border-b border-slate-200">
          <h3 className="text-sm font-semibold text-slate-900">
            Test history ({data.history?.length || 0})
          </h3>
        </div>
        {!data.history?.length ? (
          <p className="px-5 py-8 text-sm text-slate-500">
            No tests assigned to this student yet.
          </p>
        ) : (
          <table
            className="w-full text-sm"
            data-testid="student-detail-history-table"
          >
            <thead className="bg-slate-50 text-left text-slate-600">
              <tr>
                <th className="px-5 py-2 font-medium">Category</th>
                <th className="px-5 py-2 font-medium">Status</th>
                <th className="px-5 py-2 font-medium">Assigned</th>
                <th className="px-5 py-2 font-medium">Completed</th>
                <th className="px-5 py-2 font-medium text-right">Report</th>
              </tr>
            </thead>
            <tbody>
              {data.history.map((h) => {
                const isComplete = h.status === "completed";
                const busy = reportBusy === h.category_id;
                return (
                  <tr key={h.category_id} className="border-t border-slate-100">
                    <td className="px-5 py-2 text-slate-900">{h.category}</td>
                    <td className="px-5 py-2">
                      <span
                        className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-medium ${
                          STATUS_BADGES[h.status] || "bg-slate-100 text-slate-700"
                        }`}
                      >
                        {h.status}
                      </span>
                    </td>
                    <td className="px-5 py-2 text-slate-600 text-xs">
                      {fmtDate(h.assigned_at)}
                    </td>
                    <td className="px-5 py-2 text-slate-600 text-xs">
                      {fmtDate(h.completed_at)}
                    </td>
                    <td className="px-5 py-2 text-right">
                      {isComplete ? (
                        <div className="flex items-center justify-end gap-3">
                          <button
                            onClick={() => onViewReport(h.category_id)}
                            disabled={busy}
                            data-testid={`student-detail-view-report-${h.category_id}`}
                            className="text-xs font-medium text-indigo-700 hover:text-indigo-900 disabled:opacity-50"
                          >
                            {busy ? "…" : "View Report"}
                          </button>
                          <button
                            onClick={() => onDownloadReport(h.category_id)}
                            disabled={busy}
                            data-testid={`student-detail-download-report-${h.category_id}`}
                            className="text-xs font-medium text-slate-700 hover:text-slate-900 disabled:opacity-50"
                          >
                            Download
                          </button>
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {completedHistory.length === 0 && data.history?.length ? (
          <p className="px-5 py-3 text-xs text-slate-500 border-t border-slate-100">
            Reports become available after the student completes a test.
          </p>
        ) : null}
      </section>
    </div>
  );
}

function DT({ label, value, mono }) {
  return (
    <>
      <dt className="text-slate-500">{label}</dt>
      <dd
        className={
          "text-slate-900 " + (mono ? "font-mono" : "")
        }
      >
        {value || "—"}
      </dd>
    </>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 py-3 text-center">
      <div className="text-xl font-semibold text-slate-900 tabular-nums">
        {value}
      </div>
      <div className="text-xs text-slate-500">{label}</div>
    </div>
  );
}
